import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as randomUUID } from 'uuid'
import { toast } from 'react-toastify'

import MaxAppLayout from './components/layout/max-app-layout'
import MaxChatPage from './components/layout/max-chat-page'
import ChatArea from './components/layout/chat-area'
import SidebarPanel from './components/layout/sidebar-panel'
import { MAX_RUNTIME_CONFIG } from './config/max-env.config'
import {
  ActionSelections,
  AttachmentService,
  ChatMode,
  Conversation as CoreConversation,
  ConversationService,
  EphemeralChatSurface,
  ExploreCatalogCard,
  ExploreCatalogService,
  FetchHttpClient,
  FlowAction,
  FlowMetadata,
  buildExploreCardInitiationMetadata,
  isCardInitiationValid,
} from './core'
import useMaxExploreCatalog from './hooks/use-max-explore-catalog'
import { useAuth0 } from '@/auth/index'
import { Context } from '@/shared/store/reducers/global-reducer'
import useMaxChatSession from './hooks/use-max-chat-session'
import useMaxConversations from './hooks/use-max-conversations'
import useMaxMessages from './hooks/use-max-messages'
import useMaxRealtime from './hooks/use-max-realtime'
import { MaxFavoriteService } from './services/max-favorite.service'
import { fetchMaxMessages } from './services/max-chat-api.service'
import { MaxReportService } from './services/max-report.service'
import useMaxAttachmentStore, {
  areAttachmentsReadyToSend,
  areAttachmentsUploading,
  getReadyAttachments,
  hasStagedAttachments,
} from './stores/use-max-attachment-store'
import useMaxChatStore, {
  selectActiveAgentTrail,
  selectAgentTrailByMessageId,
} from './stores/use-max-chat-store'
import useMaxFlowStore from './stores/use-max-flow-store'
import useMaxLayoutStore from './stores/use-max-layout-store'
import useMaxReportStore from './stores/use-max-report-store'
import useMaxSessionStore from './stores/use-max-session-store'
import useMaxTurnStore from './stores/use-max-turn-store'
import {
  MaxChatMessage,
  MaxConversationSummary,
  MaxOutgoingSendMessagePayload,
} from './types'
import {
  budgetStepToCamelCase,
  cleanMessageId,
  findCompareReferenceMessageIdFromMessages,
  findLatestFlowMetadata,
  isBudgetCompleteMetadata,
  normalizeFlowMetadata,
} from './utils/flow'
import { maxDebug, maxMessageDebug } from './utils/max-debug'
// Side-effect import: registers AnalyzeUploadSurface in the ephemeral surface registry
import './components/chat/analyze-upload-surface'
// Load Poppins font for the MAX UI
import './max.css'

const RESUME_RECONNECT_COOLDOWN_MS = 3000

// Modes only sent by a follow-up action: they route that single request and never become the
// chat mode.
const FOLLOW_UP_ONLY_MODES: ReadonlySet<ChatMode> = new Set<ChatMode>([
  'VISUALIZE',
  'TRANS_LANG_CURR',
])

const toSummaryFromCoreConversation = (
  conversation: CoreConversation,
  options?: { pinned?: boolean }
): MaxConversationSummary => ({
  id: conversation.id,
  title: conversation.title || 'Untitled Conversation',
  preview: conversation.preview || '',
  updatedAt: new Date(conversation.updatedAt).getTime(),
  isPinned: options?.pinned ?? false,
})

const isBudgetModifyAction = (actionValue: string): boolean => actionValue.startsWith('BUDGET_MODIFY_')

const isBudgetInfoAction = (actionValue: string): boolean => actionValue.startsWith('BUDGET_INFO_')

const isBudgetReportAction = (action: FlowAction): boolean =>
  action.actionValue === 'DOWNLOAD_PDF' ||
  action.actionValue === 'DOWNLOAD_EXCEL' ||
  action.actionValue === 'EMAIL_PDF' ||
  action.actionValue === 'EMAIL_EXCEL' ||
  action.actionValue === 'CONVERT_EXCEL'

const isBudgetFollowupAction = (action: FlowAction): boolean =>
  isBudgetModifyAction(action.actionValue) ||
  isBudgetInfoAction(action.actionValue) ||
  isBudgetReportAction(action)

const headerExportReportTypeByMenuId: Record<string, string> = {
  'export-conversation-pdf': 'EXP_PDF_CONV',
  'export-conversation-docx': 'EXP_WORD_CONV',
  'export-conversation-ppt': 'EXPORT_CONV_PPT',
  'export-favorites-pdf': 'EXP_PDF_FAV',
  'export-favorites-docx': 'EXP_WORD_FAV',
}

const MaxChatRoute = () => {
  const navigate = useNavigate()
  const { clientId, chatId } = useParams<{ clientId: string; chatId: string }>()

  const { logout: auth0Logout, user: auth0User } = useAuth0()
  const { store: prodigyStore } = useContext(Context)
  const activeProdigyTenantKey =
    prodigyStore?.activeTenant?.id !== undefined && prodigyStore?.activeTenant?.id !== null
      ? String(prodigyStore.activeTenant.id)
      : null
  const { session } = useMaxChatSession()
  const messagesByConversation = useMaxChatStore((state) => state.messagesByConversation)
  const tempConversationId = useMaxChatStore((state) => state.tempConversationId)
  const awaitingConversationId = useMaxChatStore((state) => state.awaitingConversationId)
  const activeMode = useMaxChatStore((state) => state.activeMode)
  const isTypingState = useMaxChatStore((state) => state.isTyping)
  const transportError = useMaxChatStore((state) => state.transportError)
  const isLoadingMessages = useMaxChatStore((state) => state.isLoadingMessages)
  const agentTrails = useMaxChatStore((state) => state.agentTrails)
  const turns = useMaxTurnStore((state) => state.turns)
  const stagedAttachments = useMaxAttachmentStore((state) => state.attachments)
  const attachmentError = useMaxAttachmentStore((state) => state.error)
  const addAttachmentFiles = useMaxAttachmentStore((state) => state.addFiles)
  const removeAttachment = useMaxAttachmentStore((state) => state.removeAttachment)
  const clearAttachments = useMaxAttachmentStore((state) => state.clearAll)
  const markAttachmentError = useMaxAttachmentStore((state) => state.markError)

  const appendMessage = useMaxChatStore((state) => state.appendMessage)
  const setTempConversationId = useMaxChatStore((state) => state.setTempConversationId)
  const setAwaitingConversationId = useMaxChatStore((state) => state.setAwaitingConversationId)
  const setMode = useMaxChatStore((state) => state.setMode)
  const setTyping = useMaxChatStore((state) => state.setTyping)
  const startTurnWatchdog = useMaxChatStore((state) => state.startTurnWatchdog)
  const closeAgentTrailsForConversation = useMaxChatStore(
    (state) => state.closeAgentTrailsForConversation
  )
  const loadConversationMessages = useMaxChatStore((state) => state.loadMessages)
  const resetHistoryRequests = useMaxChatStore((state) => state.resetHistoryRequests)
  const setTenantScopeKey = useMaxChatStore((state) => state.setTenantScopeKey)
  const setTransportError = useMaxChatStore((state) => state.setTransportError)
  const onConversationMigrated = useMaxChatStore((state) => state.onConversationMigrated)
  const markVisualizationPending = useMaxChatStore((state) => state.markVisualizationPending)
  const setMessageFavoriteState = useMaxChatStore((state) => state.setMessageFavoriteState)

  const flowMetadataByConversation = useMaxFlowStore((state) => state.flowMetadataByConversation)
  const clearAllFlowState = useMaxFlowStore((state) => state.clearAll)
  const setFlowMetadata = useMaxFlowStore((state) => state.setFlowMetadata)
  const isAwaitingUpload = useMaxFlowStore((state) => state.isAwaitingUpload)
  const clearBudgetState = useMaxFlowStore((state) => state.clearBudgetState)
  const clearCompareReferenceMessageId = useMaxFlowStore(
    (state) => state.clearCompareReferenceMessageId
  )
  const reportsByConversation = useMaxReportStore((state) => state.reportsByConversation)
  const loadingReportsByConversation = useMaxReportStore((state) => state.loadingByConversation)
  const reportErrorsByConversation = useMaxReportStore((state) => state.errorByConversation)
  const setReportList = useMaxReportStore((state) => state.setReports)
  const setReportLoading = useMaxReportStore((state) => state.setLoading)
  const setReportError = useMaxReportStore((state) => state.setError)
  const clearAllReports = useMaxReportStore((state) => state.clearAll)
  const clearConversationReports = useMaxReportStore((state) => state.clearConversation)

  const {
    activeConversationId,
    conversations,
    pinnedConversations,
    groupedConversations,
    searchQuery,
    setConversationCollections,
    upsertConversation,
    removeConversation,
    renameConversationLocal,
    pinConversationLocal,
    unpinConversationLocal,
    setSearchQuery,
    setActiveConversationId,
    resetConversationState,
  } = useMaxConversations()

  const isSidebarCollapsed = useMaxLayoutStore((state) => state.isSidebarCollapsed)
  const collapseSidebar = useMaxLayoutStore((state) => state.collapseSidebar)
  const expandSidebar = useMaxLayoutStore((state) => state.expandSidebar)

  const [isLoadingConversations, setIsLoadingConversations] = useState(false)
  const [hasUploadsSent, setHasUploadsSent] = useState(false)

  // The conversation whose history is on screen (null for a new chat). Opening a different one
  // starts from DEFAULT; refreshing the same one, or a new chat getting its real id, keeps the mode.
  const viewedConversationIdRef = useRef<string | null>(null)
  const lastReconnectAttemptAtRef = useRef(0)
  const conversationsRequestSequenceRef = useRef(0)
  const isRouteMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isRouteMountedRef.current = false
    }
  }, [])

  // Tenant scope: the conversation state in memory belongs to one tenant. Coming back to MAX
  // under another Prodigy tenant resets it before the first paint, so nothing of the previous
  // tenant is shown while the new session and conversation list load.
  useLayoutEffect(() => {
    if (!activeProdigyTenantKey) return

    const { tenantScopeKey } = useMaxChatStore.getState()
    if (tenantScopeKey === activeProdigyTenantKey) return

    if (tenantScopeKey !== null) {
      resetHistoryRequests()
      useMaxChatStore.getState().resetConversationState()
      clearAllFlowState()
      clearAttachments()
      clearAllReports()
      viewedConversationIdRef.current = null

      if (chatId && clientId) {
        navigate(`/${clientId}/max`, { replace: true })
      }
    }

    setTenantScopeKey(activeProdigyTenantKey)
  }, [
    activeProdigyTenantKey,
    chatId,
    clearAllFlowState,
    clearAllReports,
    clearAttachments,
    clientId,
    navigate,
    resetHistoryRequests,
    setTenantScopeKey,
  ])

  const conversationService = useMemo(() => {
    const httpClient = new FetchHttpClient({
      baseURL: MAX_RUNTIME_CONFIG.api.baseUrl,
      getAuthToken: () => session.chatToken,
    })
    return new ConversationService(httpClient)
  }, [session.chatToken])

  const attachmentService = useMemo(() => {
    const httpClient = new FetchHttpClient({
      baseURL: MAX_RUNTIME_CONFIG.api.baseUrl,
      getAuthToken: () => session.chatToken,
    })
    return new AttachmentService(httpClient)
  }, [session.chatToken])

  const favoriteService = useMemo(() => {
    const httpClient = new FetchHttpClient({
      baseURL: MAX_RUNTIME_CONFIG.api.baseUrl,
      getAuthToken: () => session.chatToken,
    })
    return new MaxFavoriteService(httpClient)
  }, [session.chatToken])

  const reportService = useMemo(() => {
    const httpClient = new FetchHttpClient({
      baseURL: MAX_RUNTIME_CONFIG.api.baseUrl,
      getAuthToken: () => session.chatToken,
    })
    return new MaxReportService(httpClient)
  }, [session.chatToken])

  const exploreCatalogService = useMemo(() => {
    const httpClient = new FetchHttpClient({
      baseURL: MAX_RUNTIME_CONFIG.api.baseUrl,
      getAuthToken: () => session.chatToken,
    })
    return new ExploreCatalogService(httpClient)
  }, [session.chatToken])

  const {
    isLoading: isExploreCatalogLoading,
    error: exploreCatalogError,
    categories: exploreCatalogCategories,
    cards: exploreCatalogCards,
    filteredCards: exploreCatalogFilteredCards,
    selectedCategory: exploreCatalogSelectedCategory,
    setCategory: setExploreCatalogCategory,
    reload: reloadExploreCatalog,
  } = useMaxExploreCatalog({
    tenantId: session.isExploreModeEnabled ? session.maxTenantId : null,
    service: exploreCatalogService,
  })

  const loadConversations = useCallback(
    async (options?: { silent?: boolean }) => {
      if (session.status !== 'ready' || !session.maxTenantId) {
        return
      }

      const silent = options?.silent ?? false
      const requestTenantId = session.maxTenantId
      const requestSequence = ++conversationsRequestSequenceRef.current
      const isCurrentRequest = () =>
        isRouteMountedRef.current &&
        requestSequence === conversationsRequestSequenceRef.current &&
        useMaxSessionStore.getState().session.maxTenantId === requestTenantId

      if (!silent && isRouteMountedRef.current) {
        setIsLoadingConversations(true)
        setTransportError(null)
      }

      try {
        const data = await conversationService.getConversations(requestTenantId)
        if (!isCurrentRequest()) {
          return
        }
        setConversationCollections({
          conversations: data.conversations.map((conversation) =>
            toSummaryFromCoreConversation(conversation, { pinned: false })
          ),
          pinnedConversations: data.pinnedConversations.map((conversation) =>
            toSummaryFromCoreConversation(conversation, { pinned: true })
          ),
        })
      } catch (error) {
        if (!isCurrentRequest()) {
          return
        }
        const message = error instanceof Error ? error.message : String(error)
        if (!silent) {
          setTransportError(message)
        } else {
          maxDebug.error('Background conversation refresh failed', {
            errorMessage: message,
          })
        }
      } finally {
        if (
          !silent &&
          isRouteMountedRef.current &&
          requestSequence === conversationsRequestSequenceRef.current
        ) {
          setIsLoadingConversations(false)
        }
      }
    },
    [
      conversationService,
      session.maxTenantId,
      session.maxTenantName,
      session.status,
      setConversationCollections,
      setTransportError,
    ]
  )

  const { connectionState, isConnected, connect, disconnect, sendMessage: sendRealtimeMessage } =
    useMaxRealtime({
      onRefreshConversations: () => loadConversations({ silent: true }),
    })

  const connectionStateRef = useRef(connectionState)
  const isConnectedRef = useRef(isConnected)

  useEffect(() => {
    connectionStateRef.current = connectionState
    isConnectedRef.current = isConnected
  }, [connectionState, isConnected])

  const currentConversationId =
    activeConversationId || tempConversationId || chatId || awaitingConversationId

  const hasAttachments = useMemo(
    () => hasStagedAttachments(stagedAttachments),
    [stagedAttachments]
  )
  const readyAttachments = useMemo(
    () => getReadyAttachments(stagedAttachments),
    [stagedAttachments]
  )
  const isReadyToSendAttachments = useMemo(
    () => areAttachmentsReadyToSend(stagedAttachments),
    [stagedAttachments]
  )

  const { currentMessages, isTyping } = useMaxMessages({
    chatId,
    activeConversationId,
    tempConversationId,
    awaitingConversationId,
    isTyping: isTypingState,
    messagesByConversation,
    sessionStatus: session.status,
  })

  const activeAgentTrail = useMemo(
    () => selectActiveAgentTrail({ agentTrails, activeConversationId, tempConversationId }, turns),
    [activeConversationId, agentTrails, tempConversationId, turns]
  )

  const agentTrailForMessage = useCallback(
    (message: MaxChatMessage) =>
      selectAgentTrailByMessageId(
        { agentTrails, activeConversationId, tempConversationId },
        turns,
        message.messageId || message.id
      ),
    [activeConversationId, agentTrails, tempConversationId, turns]
  )

  const storedFlowMetadata = currentConversationId
    ? flowMetadataByConversation[currentConversationId] || null
    : null

  const activeFlowMetadata = useMemo(() => {
    const fromMessages = findLatestFlowMetadata(currentMessages)
    if (fromMessages) {
      return fromMessages
    }

    return storedFlowMetadata
  }, [currentMessages, storedFlowMetadata])

  const getExploreSelections = useMaxFlowStore((state) => state.getExploreSelections)

  const exploreInitialSelections = useMemo((): Partial<ActionSelections> | undefined => {
    const sel = getExploreSelections(currentConversationId)
    if (!sel) return undefined
    return {
      monthChosen: sel.monthChosen,
      yearChosen: sel.yearChosen,
      year1: sel.year1,
      year2: sel.year2,
    }
  }, [getExploreSelections, currentConversationId])

  const isCompareMode = activeMode === 'COMPARE'
  const isExploreMode = activeMode === 'EXPLORE'
  const isBudgetMode = activeMode === 'BUDGET'
  const isFlowActive = (isCompareMode || isExploreMode) && Boolean(activeFlowMetadata)
  const isAwaitingUploadStep =
    Boolean(currentConversationId) && isCompareMode ? isAwaitingUpload(currentConversationId) : false
  const showCompareInitiator = isCompareMode && !activeFlowMetadata && !isAwaitingUploadStep
  const showExploreInitiator = isExploreMode && !activeFlowMetadata
  const inputTextDisabled =
    (isCompareMode && !showCompareInitiator) ||
    (isExploreMode && !showExploreInitiator) ||
    isBudgetMode
  const inputAttachmentsDisabled =
    isExploreMode || isBudgetMode || (!isAwaitingUploadStep && isFlowActive)

  // Reset hasUploadsSent when the awaiting upload step or conversation changes
  useEffect(() => {
    setHasUploadsSent(false)
  }, [currentConversationId, isAwaitingUploadStep])

  const ephemeralSurface = useMemo<EphemeralChatSurface | undefined>(() => {
    if (!isCompareMode) return undefined
    if (!isAwaitingUploadStep || hasUploadsSent) return undefined
    return {
      kind: 'analyze-upload-form',
      conversationId: currentConversationId,
      flowMetadata: activeFlowMetadata,
    }
  }, [isCompareMode, isAwaitingUploadStep, hasUploadsSent, currentConversationId, activeFlowMetadata])

  const maybePinCompareReferenceMessage = useCallback(
    (conversationId?: string | null, list?: MaxChatMessage[]): string | null => {
      if (!conversationId) {
        return null
      }

      const flowState = useMaxFlowStore.getState()
      const pinned = flowState.getCompareReferenceMessageId(conversationId)
      if (pinned) {
        return pinned
      }

      const sourceMessages = list || messagesByConversation[conversationId] || []
      const derivedReference = findCompareReferenceMessageIdFromMessages(sourceMessages)
      if (!derivedReference) {
        return null
      }

      flowState.setCompareReferenceMessageId(conversationId, derivedReference)
      return derivedReference
    },
    [messagesByConversation]
  )

  const submitReportRequest = useCallback(
    async (action: FlowAction, conversationId: string, messageId: string, conversationName?: string) => {
      if (session.status !== 'ready' || !session.chatToken) {
        throw new Error('MAX session is not ready for reports')
      }
      if (!session.maxTenantId) {
        throw new Error('Tenant ID is required for reports')
      }
      if (!session.userEmail) {
        throw new Error('User email is required for reports')
      }

      await reportService.createReport({
        conversationId,
        conversationName,
        reportType: action.actionValue,
        messageId,
        tenantId: session.maxTenantId,
        email: session.userEmail,
      })
    },
    [reportService, session.chatToken, session.maxTenantId, session.status, session.userEmail]
  )

  const handleBudgetReport = useCallback(
    async (
      action: FlowAction,
      conversationId: string | null,
      latestFlowMetadata?: FlowMetadata | null,
      sourceMessageId?: string
    ): Promise<boolean> => {
      if (!conversationId) {
        setTransportError('Conversation ID is required for budget reports')
        return false
      }

      const flowState = useMaxFlowStore.getState()
      let pinnedId = flowState.getBudgetReferenceMessageId(conversationId)

      if (!pinnedId && isBudgetCompleteMetadata(latestFlowMetadata)) {
        const metaRecord = latestFlowMetadata as Record<string, unknown> | null | undefined
        const completeReference =
          cleanMessageId(
            typeof metaRecord?.budgetMessageId === 'string'
              ? metaRecord.budgetMessageId
              : undefined
          ) ||
          cleanMessageId(latestFlowMetadata?.messageId as string | undefined) ||
          cleanMessageId(sourceMessageId)

        if (completeReference) {
          flowState.setBudgetReferenceMessageId(conversationId, completeReference)
          pinnedId = completeReference
        }
      }

      if (!pinnedId) {
        setTransportError(
          'Budget reference message ID is missing. Start report from the COMPLETE budget message'
        )
        return false
      }

      try {
        await submitReportRequest(action, conversationId, pinnedId)
        setTransportError(null)
        return true
      } catch (error) {
        setTransportError(error instanceof Error ? error.message : 'Failed to submit report request')
        return false
      }
    },
    [setTransportError, submitReportRequest]
  )

  const headerTitle = useMemo(() => {
    if (!currentConversationId || currentConversationId === tempConversationId) return 'New Chat'

    const match = [...pinnedConversations, ...conversations].find(
      (conversation) => conversation.id === currentConversationId
    )

    return match?.title || 'Chat'
  }, [conversations, currentConversationId, pinnedConversations, tempConversationId])

  const currentReports = currentConversationId
    ? reportsByConversation[currentConversationId] || []
    : []
  const isSessionPending = session.status === 'idle' || session.status === 'loading'
  const currentReportsHasCached = Boolean(
    currentConversationId && reportsByConversation[currentConversationId]
  )
  const currentReportsLoading = currentConversationId
    ? loadingReportsByConversation[currentConversationId] || false
    : false
  const currentReportsError = currentConversationId
    ? reportErrorsByConversation[currentConversationId] || null
    : null

  useEffect(() => {
    setActiveConversationId(chatId || tempConversationId || null)
  }, [chatId, setActiveConversationId, tempConversationId])

  useEffect(() => {
    const isReadySession =
      session.status === 'ready' && Boolean(session.chatToken) && Boolean(session.maxTenantId)

    if (!isReadySession) {
      if (session.status === 'blocked' || session.status === 'error') {
        clearAllFlowState()
        clearAllReports()
        resetConversationState()
      }
      return
    }

    void loadConversations()
  }, [
    clearAllFlowState,
    clearAllReports,
    loadConversations,
    resetConversationState,
    session.chatToken,
    session.maxTenantId,
    session.status,
  ])

  useEffect(() => {
    if (!chatId || session.status !== 'ready' || !session.chatToken || !session.maxTenantId) {
      return
    }

    if (viewedConversationIdRef.current !== chatId) {
      setMode('DEFAULT')
    }
    viewedConversationIdRef.current = chatId

    const chatToken = session.chatToken
    void loadConversationMessages(chatId, (conversationId, tenantId) =>
      fetchMaxMessages(chatToken, tenantId, conversationId)
    )
  }, [
    chatId,
    loadConversationMessages,
    session.chatToken,
    session.maxTenantId,
    session.status,
    setMode,
  ])

  const reconnectIfNeeded = useCallback(
    async (reason: 'visibility' | 'focus' | 'online' | 'token') => {
      const token = session.chatToken
      const currentConnectionState = connectionStateRef.current
      if (!token) {
        maxDebug.warn('Realtime reconnect skipped: token missing', {
          reason,
          connectionState: currentConnectionState,
        })
        return
      }

      if (isConnectedRef.current) {
        maxDebug.debug('Realtime reconnect skipped: already connected', {
          reason,
          connectionState: currentConnectionState,
        })
        return
      }

      if (
        currentConnectionState === 'connecting' ||
        currentConnectionState === 'reconnecting'
      ) {
        maxDebug.debug('Realtime reconnect skipped: connection already in progress', {
          reason,
          connectionState: currentConnectionState,
        })
        return
      }

      const now = Date.now()
      if (now - lastReconnectAttemptAtRef.current < RESUME_RECONNECT_COOLDOWN_MS) {
        maxDebug.debug('Realtime reconnect skipped: cooldown active', {
          reason,
          connectionState: currentConnectionState,
          cooldownMs: RESUME_RECONNECT_COOLDOWN_MS,
          lastAttemptAt: lastReconnectAttemptAtRef.current,
          now,
        })
        return
      }
      lastReconnectAttemptAtRef.current = now

      try {
        await connect(MAX_RUNTIME_CONFIG.api.wsUrl, token)
      } catch (error) {
        maxDebug.error('Realtime reconnect failed', {
          reason,
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      }
    },
    [connect, session.chatToken]
  )

  useEffect(() => {
    const handleVisibilityReconnect = () => {
      if (document.visibilityState === 'visible') {
        void reconnectIfNeeded('visibility')
      }
    }

    const handleFocusReconnect = () => {
      void reconnectIfNeeded('focus')
    }

    const handleOnlineReconnect = () => {
      void reconnectIfNeeded('online')
    }

    document.addEventListener('visibilitychange', handleVisibilityReconnect)
    window.addEventListener('focus', handleFocusReconnect)
    window.addEventListener('online', handleOnlineReconnect)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityReconnect)
      window.removeEventListener('focus', handleFocusReconnect)
      window.removeEventListener('online', handleOnlineReconnect)
    }
  }, [reconnectIfNeeded])

  useEffect(() => {
    if (!session.chatToken) {
      disconnect()
      return
    }

    void reconnectIfNeeded('token')
  }, [disconnect, reconnectIfNeeded, session.chatToken])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  useEffect(() => {
    const stopMigrationListener = onConversationMigrated((conversationId) => {
      if (
        !clientId ||
        !conversationId ||
        conversationId === chatId ||
        conversationId === tempConversationId ||
        conversationId === 'temp-conversation'
      ) {
        return
      }

      viewedConversationIdRef.current = conversationId
      navigate(`/${clientId}/max/${conversationId}`)
    })

    return () => {
      stopMigrationListener()
    }
  }, [chatId, clientId, navigate, onConversationMigrated, tempConversationId])

  const collapseSidebarOnMobile = useCallback(() => {
    if (typeof window === 'undefined') return
    if (window.matchMedia('(max-width:600px)').matches) {
      collapseSidebar()
    }
  }, [collapseSidebar])

  const handleNewChat = useCallback(() => {
    if (!clientId) return
    navigate(`/${clientId}/max`)
    viewedConversationIdRef.current = null
    resetHistoryRequests()
    setActiveConversationId(null)
    setTempConversationId(null)
    setAwaitingConversationId(null)
    setMode('DEFAULT')
    setTyping(false)
    setTransportError(null)
    clearAttachments()
    collapseSidebarOnMobile()
  }, [
    clearAttachments,
    clientId,
    collapseSidebarOnMobile,
    navigate,
    resetHistoryRequests,
    setActiveConversationId,
    setAwaitingConversationId,
    setMode,
    setTempConversationId,
    setTyping,
    setTransportError,
  ])

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      if (!clientId) return
      navigate(`/${clientId}/max/${conversationId}`)
      setTransportError(null)
      collapseSidebarOnMobile()
    },
    [clientId, collapseSidebarOnMobile, navigate, setTransportError]
  )

  const handleRenameConversation = useCallback(
    async (conversation: MaxConversationSummary, nextTitle: string) => {
      if (session.status !== 'ready' || !session.maxTenantId) return

      const trimmed = nextTitle.trim()
      if (!trimmed || trimmed === conversation.title) return

      try {
        await conversationService.updateConversationTitle(session.maxTenantId, conversation.id, trimmed)
        renameConversationLocal({
          conversationId: conversation.id,
          title: trimmed,
        })
        setTransportError(null)
      } catch (error) {
        setTransportError(error instanceof Error ? error.message : String(error))
      }
    },
    [conversationService, renameConversationLocal, session.maxTenantId, session.status, setTransportError]
  )

  const handleTogglePinConversation = useCallback(
    async (conversation: MaxConversationSummary) => {
      if (session.status !== 'ready' || !session.maxTenantId) return

      try {
        if (conversation.isPinned) {
          await conversationService.unpinConversation(session.maxTenantId, conversation.id)
          unpinConversationLocal(conversation.id)
        } else {
          await conversationService.pinConversation(session.maxTenantId, conversation.id)
          pinConversationLocal(conversation.id)
        }
        setTransportError(null)
      } catch (error) {
        setTransportError(error instanceof Error ? error.message : String(error))
      }
    },
    [
      conversationService,
      pinConversationLocal,
      session.maxTenantId,
      session.status,
      setTransportError,
      unpinConversationLocal,
    ]
  )

  const handleDeleteConversation = useCallback(
    async (conversation: MaxConversationSummary) => {
      if (session.status !== 'ready' || !session.maxTenantId) return

      try {
        await conversationService.deleteConversation(session.maxTenantId, conversation.id)
        removeConversation(conversation.id)
        clearConversationReports(conversation.id)

        if (chatId === conversation.id && clientId) {
          navigate(`/${clientId}/max`)
        }
        setTransportError(null)
      } catch (error) {
        setTransportError(error instanceof Error ? error.message : String(error))
      }
    },
    [
      chatId,
      clientId,
      clearConversationReports,
      conversationService,
      navigate,
      removeConversation,
      session.maxTenantId,
      session.status,
      setTransportError,
    ]
  )

  const handleAttachFiles = useCallback(
    (fileInput: FileList | File[]) => {
      addAttachmentFiles(fileInput, (file, onProgress) => attachmentService.stageFile(file, onProgress))
    },
    [addAttachmentFiles, attachmentService]
  )

  const sendChatMessage = useCallback(
    async (params: {
      content: string
      attachments?: typeof readyAttachments
      mode?: ChatMode
      flowMetadata?: FlowMetadata | null
      isFlow?: boolean
      allowEmpty?: boolean
      promptOverride?: string | null
    }): Promise<boolean> => {
      const {
        content,
        attachments = [],
        mode = activeMode,
        flowMetadata,
        isFlow = false,
        allowEmpty = false,
        promptOverride,
      } = params

      const trimmedContent = content.trim()
      const normalizedFlow = normalizeFlowMetadata(flowMetadata) || undefined
      if (!allowEmpty && !trimmedContent) {
        maxMessageDebug.event('Route sendChatMessage rejected empty content', {
          mode,
          conversationId: chatId || tempConversationId || null,
          allowEmpty,
          attachmentsCount: attachments.length,
        })
        return false
      }

      if (mode === 'EXPLORE' && !session.isExploreModeEnabled) {
        setTransportError('Explore mode is not enabled for the current tenant.')
        return false
      }

      if (session.status !== 'ready' || !session.chatToken || !session.maxTenantId) {
        maxMessageDebug.error('Route sendChatMessage blocked: session not ready', {
          mode,
          sessionStatus: session.status,
          hasChatToken: Boolean(session.chatToken),
          hasTenantId: Boolean(session.maxTenantId),
          conversationId: chatId || tempConversationId || null,
        })
        setTransportError('MAX session is not ready. Message was not sent.')
        return false
      }

      let targetConversationId = chatId || tempConversationId
      if (!targetConversationId) {
        targetConversationId = randomUUID()
        setTempConversationId(targetConversationId)
        upsertConversation({
          id: targetConversationId,
          title: (trimmedContent || content).slice(0, 50) || 'New Conversation',
          preview: (trimmedContent || content).slice(0, 120),
          updatedAt: Date.now(),
          isTemporary: true,
        })
      }

      if (mode === 'VISUALIZE') {
        markVisualizationPending(targetConversationId)
      }

      const promptToSend =
        promptOverride !== undefined && promptOverride !== null ? promptOverride : trimmedContent

      // One turn per send, attributed by the frontend-generated clientRequestId and stamped with
      // the tenant of the moment of sending.
      const clientRequestId = randomUUID()
      const turnStore = useMaxTurnStore.getState()
      turnStore.register({
        clientRequestId,
        tenantId: session.maxTenantId,
        conversationId: chatId || null,
        tempConversationId: chatId ? null : targetConversationId,
      })

      const outgoingPayload: MaxOutgoingSendMessagePayload = {
        action: 'sendMessage',
        token: session.chatToken,
        tenantId: session.maxTenantId,
        prompt: promptToSend,
        conversationId: chatId || undefined,
        tempConversationId: chatId ? undefined : targetConversationId,
        mode,
        attachments,
        flow_metadata: normalizedFlow,
        is_flow: isFlow || undefined,
        clientRequestId,
      }

      maxMessageDebug.event('Route sendChatMessage prepared outbound payload', {
        mode,
        conversationId: outgoingPayload.conversationId || null,
        tempConversationId: outgoingPayload.tempConversationId || null,
        promptLength: outgoingPayload.prompt.length,
        attachmentsCount: attachments.length,
        isFlow,
        hasFlowMetadata: Boolean(normalizedFlow),
      })

      const sent = sendRealtimeMessage(outgoingPayload)
      if (!sent) {
        maxMessageDebug.error('Route sendChatMessage aborted after websocket send failure', {
          mode,
          conversationId: outgoingPayload.conversationId || null,
          tempConversationId: outgoingPayload.tempConversationId || null,
        })
        turnStore.remove(clientRequestId)
        closeAgentTrailsForConversation(chatId || null, 'error')
        return false
      }

      const displayContent =
        trimmedContent ||
        (typeof promptOverride === 'string' ? promptOverride.trim() : '') ||
        content

      const optimisticUserMessage: MaxChatMessage = {
        id: randomUUID(),
        conversationId: targetConversationId,
        role: 'user',
        content: displayContent,
        createdAt: Date.now(),
        attachments: attachments.length ? attachments : undefined,
        isFlow,
        flowMetadata: normalizedFlow,
      }

      appendMessage(targetConversationId, optimisticUserMessage)
      turnStore.setUserMessage(clientRequestId, optimisticUserMessage)
      maxMessageDebug.event('Route sendChatMessage queued optimistic user message', {
        mode,
        conversationId: targetConversationId,
        messageId: optimisticUserMessage.id,
        createdAt: optimisticUserMessage.createdAt,
        isFlow,
      })

      if (normalizedFlow) {
        setFlowMetadata(targetConversationId, normalizedFlow)
      }

      setAwaitingConversationId(targetConversationId)
      if (!FOLLOW_UP_ONLY_MODES.has(mode)) {
        setMode(mode)
      }
      setTyping(true)
      // Per-turn inactivity watchdog: any backend signal of the turn resets it (D-10).
      startTurnWatchdog(chatId || targetConversationId)
      setTransportError(null)
      if (attachments.length > 0) {
        clearAttachments()
      }

      return true
    },
    [
      activeMode,
      appendMessage,
      chatId,
      clearAttachments,
      closeAgentTrailsForConversation,
      sendRealtimeMessage,
      session.chatToken,
      session.isExploreModeEnabled,
      session.maxTenantId,
      session.status,
      markVisualizationPending,
      setAwaitingConversationId,
      setFlowMetadata,
      setMode,
      setTempConversationId,
      setTyping,
      setTransportError,
      startTurnWatchdog,
      tempConversationId,
      upsertConversation,
    ]
  )

  const handleSend = useCallback(async (content: string): Promise<boolean> => {
    if (hasAttachments && !isReadyToSendAttachments) {
      markAttachmentError('Please wait for attachments to finish uploading before sending')
      return false
    }

    const outgoingAttachments = inputAttachmentsDisabled
      ? []
      : hasAttachments
      ? readyAttachments
      : []
    const allowEmpty = outgoingAttachments.length > 0 && isAwaitingUploadStep
    const flowMetadataToSend =
      isCompareMode && isAwaitingUploadStep ? activeFlowMetadata || undefined : undefined

    const sent = await sendChatMessage({
      content,
      attachments: outgoingAttachments,
      mode: activeMode,
      flowMetadata: flowMetadataToSend,
      allowEmpty,
    })

    if (sent && isAwaitingUploadStep) {
      setHasUploadsSent(true)
    }

    return sent
  }, [
    activeFlowMetadata,
    activeMode,
    hasAttachments,
    inputAttachmentsDisabled,
    isAwaitingUploadStep,
    isCompareMode,
    isReadyToSendAttachments,
    markAttachmentError,
    readyAttachments,
    sendChatMessage,
  ])

  const handleSubmitEphemeralSurface = useCallback(async () => {
    await handleSend('')
  }, [handleSend])

  const handleStartCompare = useCallback(() => {
    clearCompareReferenceMessageId(currentConversationId)
    return sendChatMessage({
      content: 'Start Analyze flow',
      mode: 'COMPARE',
      isFlow: true,
    })
  }, [clearCompareReferenceMessageId, currentConversationId, sendChatMessage])

  const handleStartExplore = useCallback(() => {
    if (!session.isExploreModeEnabled) {
      setTransportError('Explore mode is not enabled for the current tenant.')
      return Promise.resolve(false)
    }

    return sendChatMessage({
      content: 'Start Explore flow',
      mode: 'EXPLORE',
      isFlow: true,
      flowMetadata: {},
    })
  }, [sendChatMessage, session.isExploreModeEnabled, setTransportError])

  const handleStartExploreFromCard = useCallback(
    (card: ExploreCatalogCard, selections?: Partial<ActionSelections>) => {
      if (!session.isExploreModeEnabled) {
        setTransportError('Explore mode is not enabled for the current tenant.')
        return Promise.resolve(false)
      }

      if (!isCardInitiationValid(card)) {
        setTransportError('Explore card is not configured correctly.')
        return Promise.resolve(false)
      }

      const flowMetadata = buildExploreCardInitiationMetadata(card, selections)

      const shouldPersistInitialSelections =
        card.initiation.type === 'query' &&
        Boolean(card.initiation.requiredCondition && selections)

      if (shouldPersistInitialSelections && selections && card.initiation.requiredCondition) {
        const flowStore = useMaxFlowStore.getState()
        flowStore.setExploreSelections(currentConversationId, {
          condition: card.initiation.requiredCondition,
          monthChosen: selections.monthChosen,
          yearChosen: selections.yearChosen,
          year1: selections.year1,
          year2: selections.year2,
        })
      }

      return sendChatMessage({
        content: card.initiation.prompt || card.title,
        mode: 'EXPLORE',
        isFlow: true,
        flowMetadata,
      })
    },
    [
      currentConversationId,
      sendChatMessage,
      session.isExploreModeEnabled,
      setTransportError,
    ]
  )

  const handleStartBudget = useCallback(() => {
    clearBudgetState(currentConversationId)
    return sendChatMessage({
      content: 'Start Budget flow',
      mode: 'BUDGET',
      isFlow: true,
    })
  }, [clearBudgetState, currentConversationId, sendChatMessage])

  const handleModeSelect = useCallback(
    (mode: ChatMode) => {
      const flow = activeFlowMetadata?.flow
      const isExploreFlowActive =
        activeMode === 'EXPLORE' && typeof flow === 'string' && flow.toUpperCase() === 'EXPLORE'

      if (isExploreFlowActive && mode !== 'EXPLORE') {
        return
      }

      if (mode === 'EXPLORE' && !session.isExploreModeEnabled) {
        setTransportError('Explore mode is not enabled for the current tenant.')
        return
      }

      setMode(mode)
      setTransportError(null)
    },
    [activeFlowMetadata, activeMode, session.isExploreModeEnabled, setMode, setTransportError]
  )

  const handleFlowActionSelected = useCallback(
    async (payload: {
      action: FlowAction
      message: MaxChatMessage
      selections?: ActionSelections
    }) => {
      const conversationId = payload.message.conversationId || currentConversationId
      const latestFlowMetadata =
        normalizeFlowMetadata(payload.message.flowMetadata) ||
        normalizeFlowMetadata(activeFlowMetadata) ||
        undefined
      const sourceMessageId = payload.message.messageId || payload.message.id

      maxMessageDebug.event('Route flow action selected', {
        conversationId,
        sourceMessageId,
        actionValue: payload.action.actionValue,
        actionType: payload.action.actionType,
        flow: latestFlowMetadata?.flow || null,
        selections: payload.selections || null,
      })

      if (
        ((payload.action.actionType === 'mode' && payload.action.actionValue === 'EXPLORE') ||
          latestFlowMetadata?.flow?.toUpperCase() === 'EXPLORE') &&
        !session.isExploreModeEnabled
      ) {
        setTransportError('Explore mode is not enabled for the current tenant.')
        return
      }

      const baseMeta = latestFlowMetadata ? { ...latestFlowMetadata } : {}
      delete (baseMeta as Record<string, unknown>).availableActions
      delete (baseMeta as Record<string, unknown>).actionsAvailable

      const flowType = latestFlowMetadata?.flow?.toUpperCase()
      const isModeAction = payload.action.actionType === 'mode'

      // The mode of a 'mode' action is its value; any other action keeps the flow of its source
      // message, and without a flow it is a plain OpenChat question.
      let modeToUse: ChatMode = 'DEFAULT'
      if (isModeAction) {
        modeToUse = payload.action.actionValue as ChatMode
      } else if (flowType) {
        modeToUse = flowType as ChatMode
      }

      // A suggested question of an OpenChat answer: send only its prompt, without flow state.
      if (modeToUse === 'DEFAULT') {
        await sendChatMessage({
          content: payload.action.prompt || payload.action.actionText,
          mode: 'DEFAULT',
          flowMetadata: {},
          isFlow: false,
        })
        return
      }

      const isExploreFlow = flowType === 'EXPLORE'
      const isCompareFlow = flowType === 'COMPARE'
      const isBudgetFlow = flowType === 'BUDGET'
      const isExploreBackAction =
        modeToUse === 'EXPLORE' &&
        ((payload.action.actionType === 'mode' && payload.action.actionValue === 'EXPLORE') ||
          (payload.action.metadata?.menuType === 'menu' &&
            payload.action.metadata?.targetMenu === 'main_menu') ||
          payload.action.actionValue.toUpperCase().includes('BACK_TO_MAIN'))

      if (isExploreBackAction) {
        const prompt = payload.action.actionText || payload.action.prompt || 'Go back to main menu'
        await sendChatMessage({
          content: prompt,
          mode: 'EXPLORE',
          flowMetadata: {},
          isFlow: false,
        })
        return
      }

      if (isBudgetFlow && isBudgetReportAction(payload.action)) {
        await handleBudgetReport(payload.action, conversationId, latestFlowMetadata, sourceMessageId)
        return
      }

      const mergedMetadata: FlowMetadata = {
        ...baseMeta,
        selectedAction: payload.action,
      }

      if (isExploreFlow && payload.action.actionValue === 'VISUALIZE') {
        const messageId = cleanMessageId(sourceMessageId)
        if (messageId) {
          mergedMetadata.messageId = messageId
        }
      }

      if (isCompareFlow) {
        const compareReference = maybePinCompareReferenceMessage(conversationId)
        const phase = latestFlowMetadata?.phase
        const requiresCompareReference =
          typeof phase === 'string' && phase.toUpperCase() === 'FOLLOWUP'

        if (compareReference) {
          mergedMetadata.messageId = compareReference
        } else if (requiresCompareReference) {
          setTransportError(
            'Analyze reference message ID is missing. Start follow-up actions from the original analysis response'
          )
          return
        }
      }

      if (modeToUse === 'EXPLORE') {
        const flowState = useMaxFlowStore.getState()
        const menuType = payload.action.metadata?.menuType

        delete mergedMetadata.monthChosen
        delete mergedMetadata.yearChosen
        delete mergedMetadata.year1
        delete mergedMetadata.year2
        delete mergedMetadata.parameters

        if (menuType === 'menu') {
          mergedMetadata.currentMenuId = latestFlowMetadata?.currentMenuId || 'main_menu'
          mergedMetadata.selectedMenuItemId = payload.action.actionValue
        } else if (menuType === 'query') {
          mergedMetadata.currentMenuId = latestFlowMetadata?.currentMenuId || 'main_menu'
          mergedMetadata.selectedMenuItemId = payload.action.actionValue

          const requiredCondition =
            payload.action.requiredCondition || payload.action.metadata?.requiredCondition
          if (requiredCondition === 'month_and_year' && payload.selections) {
            mergedMetadata.monthChosen = payload.selections.monthChosen
            mergedMetadata.yearChosen = payload.selections.yearChosen
            flowState.setExploreSelections(conversationId, {
              condition: 'month_and_year',
              monthChosen: payload.selections.monthChosen,
              yearChosen: payload.selections.yearChosen,
            })
          } else if (requiredCondition === 'year1_and_year2' && payload.selections) {
            mergedMetadata.year1 = payload.selections.year1
            mergedMetadata.year2 = payload.selections.year2
            flowState.setExploreSelections(conversationId, {
              condition: 'year1_and_year2',
              year1: payload.selections.year1,
              year2: payload.selections.year2,
            })
          }
        }
      }

      if (payload.selections?.translationLanguage) {
        mergedMetadata.translationLanguage = payload.selections.translationLanguage
      }
      if (payload.selections?.conversionChosen) {
        mergedMetadata.conversionChosen = payload.selections.conversionChosen
      }

      if (isBudgetFlow && conversationId) {
        const flowState = useMaxFlowStore.getState()

        if (latestFlowMetadata?.answers) {
          flowState.setBudgetAnswers(conversationId, latestFlowMetadata.answers)
        }

        const isInitialWizardAction = latestFlowMetadata?.step && !isBudgetFollowupAction(payload.action)
        if (isInitialWizardAction && latestFlowMetadata.step) {
          flowState.setBudgetAnswer(
            conversationId,
            budgetStepToCamelCase(latestFlowMetadata.step),
            payload.action.actionValue
          )
        }

        const cachedAnswers = flowState.getBudgetAnswers(conversationId)
        if (Object.keys(cachedAnswers).length > 0) {
          mergedMetadata.answers = cachedAnswers
        }

        let pinnedBudgetReference = flowState.getBudgetReferenceMessageId(conversationId)
        if (!pinnedBudgetReference && isBudgetCompleteMetadata(latestFlowMetadata)) {
          const metaRecord = latestFlowMetadata as Record<string, unknown> | null | undefined
          const completeReference =
            cleanMessageId(
              typeof metaRecord?.budgetMessageId === 'string'
                ? metaRecord.budgetMessageId
                : undefined
            ) ||
            cleanMessageId(latestFlowMetadata?.messageId) ||
            cleanMessageId(sourceMessageId)

          if (completeReference) {
            flowState.setBudgetReferenceMessageId(conversationId, completeReference)
            pinnedBudgetReference = completeReference
          }
        }

        if (isBudgetFollowupAction(payload.action)) {
          if (!pinnedBudgetReference) {
            setTransportError(
              'Budget reference message ID is missing. Start follow-up actions from the COMPLETE budget message'
            )
            return
          }

          mergedMetadata.messageId = pinnedBudgetReference
        }
      }

      const prompt =
        isBudgetFlow && payload.selections?.budgetPrompt
          ? payload.selections.budgetPrompt
          : payload.action.prompt || payload.action.actionText

      await sendChatMessage({
        content: prompt,
        mode: modeToUse,
        flowMetadata: mergedMetadata,
        isFlow: false,
      })
    },
    [
      activeFlowMetadata,
      currentConversationId,
      handleBudgetReport,
      maybePinCompareReferenceMessage,
      sendChatMessage,
      session.isExploreModeEnabled,
      setTransportError,
    ]
  )

  const handleToolkitFavorite = useCallback(
    async (payload: { message: MaxChatMessage }) => {
      if (session.status !== 'ready' || !session.chatToken) {
        setTransportError('MAX session is not ready for favorites')
        toast.error('MAX session is not ready for favorites')
        return
      }
      if (!session.maxTenantId) {
        setTransportError('Tenant ID is required')
        toast.error('Tenant ID is required')
        return
      }

      const messageId = payload.message.messageId || payload.message.id
      const conversationId = payload.message.conversationId || currentConversationId
      if (!messageId || !conversationId) {
        setTransportError('Message ID and conversation ID are required')
        toast.error('Message ID and conversation ID are required')
        return
      }

      const wasFavorited = Boolean(payload.message.fav)
      setMessageFavoriteState(conversationId, messageId, !wasFavorited)

      try {
        if (wasFavorited) {
          await favoriteService.unfavoriteMessage({
            messageId,
            conversationId,
            tenantId: session.maxTenantId,
            email: session.userEmail || undefined,
          })
          toast.info('Message unfavorited')
        } else {
          await favoriteService.favoriteMessage({
            messageId,
            conversationId,
            tenantId: session.maxTenantId,
            email: session.userEmail || undefined,
          })
          toast.success('Message favorited')
        }

        setTransportError(null)
      } catch (error) {
        setMessageFavoriteState(conversationId, messageId, wasFavorited)
        const message = error instanceof Error ? error.message : 'Failed to update favorite'
        setTransportError(message)
        toast.error(message)
      }
    },
    [
      currentConversationId,
      favoriteService,
      session.chatToken,
      session.maxTenantId,
      session.status,
      session.userEmail,
      setMessageFavoriteState,
      setTransportError,
    ]
  )

  const handleToolkitShare = useCallback(() => {
    toast.info('shared')
  }, [])

  const handleToolkitRetry = useCallback(() => {
    toast.info('retry')
  }, [])

  const handleToolkitExportBudget = useCallback(
    async (payload: { message: MaxChatMessage; action: FlowAction }) => {
      const conversationId = payload.message.conversationId || currentConversationId
      const messageId = cleanMessageId(payload.message.messageId || payload.message.id)

      if (!conversationId) {
        setTransportError('Conversation ID is required for reports')
        toast.error('Conversation ID is required for reports')
        return
      }

      if (!messageId) {
        setTransportError('Message ID is required for report export')
        toast.error('Message ID is required for report export')
        return
      }

      try {
        await submitReportRequest(payload.action, conversationId, messageId)
        setTransportError(null)
        toast.success('Budget Report is being generated. Please wait...')
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to submit report request'
        setTransportError(message)
        toast.error(message)
      }
    },
    [currentConversationId, setTransportError, submitReportRequest]
  )

  const handleToolkitExportReport = useCallback(
    async (payload: { message: MaxChatMessage; action: FlowAction }) => {
      const conversationId = payload.message.conversationId || currentConversationId
      const messageId = cleanMessageId(payload.message.messageId || payload.message.id)

      if (!conversationId) {
        setTransportError('Conversation ID is required for reports')
        toast.error('Conversation ID is required for reports')
        return
      }

      if (!messageId) {
        setTransportError('Message ID is required for report export')
        toast.error('Message ID is required for report export')
        return
      }

      try {
        await submitReportRequest(payload.action, conversationId, messageId)
        setTransportError(null)
        toast.success('Report is being generated. Please wait...')
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to submit report request'
        setTransportError(message)
        toast.error(message)
      }
    },
    [currentConversationId, setTransportError, submitReportRequest]
  )

  const fetchReportsForConversation = useCallback(
    async (conversationId: string) => {
      if (session.status !== 'ready' || !session.chatToken) {
        throw new Error('MAX session is not ready for reports')
      }
      if (!session.maxTenantId) {
        throw new Error('Tenant ID is required to fetch reports')
      }

      setReportLoading(conversationId, true)
      setReportError(conversationId, null)

      try {
        const reports = await reportService.getReports(conversationId, session.maxTenantId)
        setReportList(conversationId, reports)
        return reports
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch reports'
        setReportError(conversationId, message)
        throw error
      } finally {
        setReportLoading(conversationId, false)
      }
    },
    [
      reportService,
      session.chatToken,
      session.maxTenantId,
      session.status,
      setReportError,
      setReportList,
      setReportLoading,
    ]
  )

  const handleDownloadReport = useCallback(
    async (reportId: string, reportTenantId?: string) => {
      if (session.status !== 'ready' || !session.chatToken) {
        throw new Error('MAX session is not ready for reports')
      }

      // The report carries its own tenant; the selected tenant is only the fallback.
      return reportService.downloadReport(reportId, reportTenantId || session.maxTenantId || '')
    },
    [reportService, session.chatToken, session.maxTenantId, session.status]
  )

  const queueConversationReport = useCallback(
    async (
      reportType: string,
      conversationId?: string | null,
      conversationName?: string | null
    ): Promise<boolean> => {
      if (!conversationId) {
        setTransportError('Conversation ID is required for reports')
        return false
      }
      if (session.status !== 'ready' || !session.chatToken) {
        setTransportError('MAX session is not ready for reports')
        return false
      }
      if (!session.maxTenantId) {
        setTransportError('Tenant ID is required for reports')
        return false
      }
      if (!session.userEmail) {
        setTransportError('User email is required for reports')
        return false
      }

      try {
        await reportService.createReport({
          conversationId,
          conversationName: conversationName || undefined,
          reportType,
          tenantId: session.maxTenantId,
          email: session.userEmail,
        })
        setTransportError(null)
        return true
      } catch (error) {
        setTransportError(error instanceof Error ? error.message : 'Failed to submit report request')
        return false
      }
    },
    [
      reportService,
      session.chatToken,
      session.maxTenantId,
      session.status,
      session.userEmail,
      setTransportError,
    ]
  )

  const handleSignOut = useCallback(() => {
    auth0Logout()
  }, [auth0Logout])

  const handleHeaderOptionSelect = useCallback(
    async (id: string) => {
      const reportType = headerExportReportTypeByMenuId[id]
      if (!reportType) {
        toast.info('Unsupported export option')
        return
      }

      const success = await queueConversationReport(
        reportType,
        currentConversationId,
        headerTitle === 'New Chat' ? null : headerTitle
      )

      if (!success) {
        toast.error(useMaxChatStore.getState().transportError || 'Something went wrong')
        return
      }

      toast.success('Report is being generated. Please wait...')
    },
    [currentConversationId, headerTitle, queueConversationReport]
  )

  return (
    <MaxAppLayout>
      <MaxChatPage
        isSidebarCollapsed={isSidebarCollapsed}
        onExpandSidebar={expandSidebar}
        sidebar={
          <SidebarPanel
            searchQuery={searchQuery}
            isLoadingConversations={isSessionPending || isLoadingConversations}
            groupedConversationSummaries={groupedConversations}
            activeConversationId={currentConversationId}
            userName={auth0User?.name || null}
            userEmail={session.userEmail || auth0User?.email || null}
            tenantName={session.maxTenantName || null}
            onSearchChange={setSearchQuery}
            onNewChat={handleNewChat}
            onSelectConversation={handleSelectConversation}
            onRenameConversation={handleRenameConversation}
            onTogglePinConversation={handleTogglePinConversation}
            onDeleteConversation={handleDeleteConversation}
            onCollapseSidebar={collapseSidebar}
            onSignOut={handleSignOut}
          />
        }
        chatArea={
          <ChatArea
            headerTitle={headerTitle}
            currentConversationId={currentConversationId}
            currentMessages={currentMessages}
            connectionState={connectionState}
            isTyping={isTyping}
            activeAgentTrail={activeAgentTrail}
            agentTrailForMessage={agentTrailForMessage}
            isLoadingMessages={isLoadingMessages}
            sessionStatus={session.status}
            sessionError={session.error}
            transportError={transportError}
            activeMode={activeMode}
            isExploreModeEnabled={session.isExploreModeEnabled}
            activeFlowMetadata={activeFlowMetadata}
            isAwaitingUploadStep={isAwaitingUploadStep}
            ephemeralSurface={ephemeralSurface}
            textDisabled={inputTextDisabled}
            attachmentsDisabled={inputAttachmentsDisabled}
            onSend={handleSend}
            onStartCompare={handleStartCompare}
            onStartExplore={handleStartExplore}
            onStartBudget={handleStartBudget}
            onModeSelect={handleModeSelect}
            onSelectFlowAction={handleFlowActionSelected}
            onToolkitFavorite={handleToolkitFavorite}
            onToolkitShare={handleToolkitShare}
            onToolkitRetry={handleToolkitRetry}
            onToolkitExportBudget={handleToolkitExportBudget}
            onToolkitExportReport={handleToolkitExportReport}
            onSubmitEphemeralSurface={handleSubmitEphemeralSurface}
            onAttachFiles={handleAttachFiles}
            exploreIsLoading={isExploreCatalogLoading}
            exploreError={exploreCatalogError}
            exploreCategories={exploreCatalogCategories}
            exploreCards={exploreCatalogCards}
            exploreFilteredCards={exploreCatalogFilteredCards}
            exploreSelectedCategory={exploreCatalogSelectedCategory}
            onExploreSelectCategory={setExploreCatalogCategory}
            onExploreSelectCard={(card) => { void handleStartExploreFromCard(card) }}
            onExploreReload={reloadExploreCatalog}
            onExploreApplyCard={(card, selections) => { void handleStartExploreFromCard(card, selections) }}
            exploreInitialSelections={exploreInitialSelections}
            reports={currentReports}
            reportsHasCached={currentReportsHasCached}
            reportsLoading={currentReportsLoading}
            reportsError={currentReportsError}
            onHeaderOptionSelect={handleHeaderOptionSelect}
            onFetchReports={fetchReportsForConversation}
            onDownloadReport={handleDownloadReport}
          />
        }
      />
    </MaxAppLayout>
  )
}

export default MaxChatRoute
